import { getAuthenticatedFetch } from "../../msal/authenticatedFetchService";
import {
    BulkProvisioningJobStatusDto,
    CreateTemplateTypeRequest,
    EligibleTenantDto,
    SurveyJSProvisioningRecordDto,
    SurveyJSSyncMetadataDto,
    SurveyJSTemplateDto,
    SurveyJSTemplateTypeDto,
    TemplateProvisioningResult,
    TenantProvisioningAttemptDto,
    TenantProvisioningRowDto,
} from "../types/SurveyJSTemplate";

const baseUrl = `${process.env.REACT_APP_SELF_SERVICE_CARECOMPLIANCE_API_URL}/SurveyJSTemplate`;

class SurveyJSTemplateService {
    // ─── Template Types ───
    async listTypes(includeRetired = false): Promise<SurveyJSTemplateTypeDto[]> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/types?includeRetired=${includeRetired}`);
        if (!response.ok) throw new Error(await this.extractError(response, `List types failed (${response.status})`));
        return await response.json();
    }

    async createType(request: CreateTemplateTypeRequest): Promise<SurveyJSTemplateTypeDto> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/types`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(request),
        });
        if (!response.ok) throw new Error(await this.extractError(response, `Create type failed (${response.status})`));
        return await response.json();
    }

    async retireType(module: string, typeKey: string): Promise<void> {
        const response = await getAuthenticatedFetch()(
            `${baseUrl}/types/${encodeURIComponent(module)}/${encodeURIComponent(typeKey)}/retire`,
            { method: "POST" }
        );
        if (!response.ok) throw new Error(await this.extractError(response, `Retire type failed (${response.status})`));
    }

    // ─── Templates ───
    async listTemplatesByModule(module: string): Promise<SurveyJSTemplateDto[]> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/by-module/${encodeURIComponent(module)}`);
        if (!response.ok) throw new Error(await this.extractError(response, `List templates failed (${response.status})`));
        return await response.json();
    }

    async listTemplatesByType(typeKey: string): Promise<SurveyJSTemplateDto[]> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/by-type/${encodeURIComponent(typeKey)}`);
        if (!response.ok) throw new Error(await this.extractError(response, `List templates by type failed (${response.status})`));
        return await response.json();
    }

    async listActiveTemplates(): Promise<SurveyJSTemplateDto[]> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/active-templates`);
        if (!response.ok) throw new Error(await this.extractError(response, `List active templates failed (${response.status})`));
        return await response.json();
    }

    async uploadTemplate(payload: {
        templateName: string;
        version: string;
        file: File;
        isNewType: boolean;
        // When isNewType=false:
        templateType?: string;
        // When isNewType=true:
        newTypeModule?: string;
        newTypeModuleDisplayName?: string;
        newTypeDisplayName?: string;
        newTypeFeatureName?: string;
        newTypeCodeSuffix?: string;
        newTypeDescription?: string;
    }): Promise<SurveyJSTemplateDto> {
        const form = new FormData();
        form.append("file", payload.file);
        form.append("templateName", payload.templateName);
        form.append("version", payload.version);
        form.append("isNewType", payload.isNewType ? "true" : "false");
        if (payload.isNewType) {
            form.append("newTypeModule", payload.newTypeModule ?? "");
            form.append("newTypeModuleDisplayName", payload.newTypeModuleDisplayName ?? "");
            form.append("newTypeDisplayName", payload.newTypeDisplayName ?? "");
            form.append("newTypeFeatureName", payload.newTypeFeatureName ?? "");
            form.append("newTypeCodeSuffix", payload.newTypeCodeSuffix ?? "");
            form.append("newTypeDescription", payload.newTypeDescription ?? "");
        } else {
            form.append("templateType", payload.templateType ?? "");
        }

        const response = await getAuthenticatedFetch()(`${baseUrl}/upload`, { method: "POST", body: form });
        if (!response.ok) throw new Error(await this.extractError(response, `Upload failed (${response.status})`));
        return await response.json();
    }

    async activateTemplate(templateGuid: string): Promise<void> {
        const response = await getAuthenticatedFetch()(
            `${baseUrl}/templates/${encodeURIComponent(templateGuid)}/activate`,
            { method: "POST" }
        );
        if (!response.ok) throw new Error(await this.extractError(response, `Activate failed (${response.status})`));
    }

    async deactivateTemplate(templateGuid: string): Promise<void> {
        const response = await getAuthenticatedFetch()(
            `${baseUrl}/templates/${encodeURIComponent(templateGuid)}/deactivate`,
            { method: "POST" }
        );
        if (!response.ok) throw new Error(await this.extractError(response, `Deactivate failed (${response.status})`));
    }

    async getProvisioningRecords(templateGuid: string): Promise<SurveyJSProvisioningRecordDto[]> {
        const response = await getAuthenticatedFetch()(
            `${baseUrl}/templates/${encodeURIComponent(templateGuid)}/provisioning`
        );
        if (!response.ok) throw new Error(await this.extractError(response, `Records failed (${response.status})`));
        return await response.json();
    }

    async getTenantProvisioning(templateGuid: string): Promise<TenantProvisioningRowDto[]> {
        const response = await getAuthenticatedFetch()(
            `${baseUrl}/templates/${encodeURIComponent(templateGuid)}/tenant-provisioning`
        );
        if (!response.ok) throw new Error(await this.extractError(response, `Tenant provisioning failed (${response.status})`));
        return await response.json();
    }

    async getTenantProvisioningHistory(
        templateGuid: string, corpSiteName: string, clientId: string | number
    ): Promise<TenantProvisioningAttemptDto[]> {
        const url = `${baseUrl}/templates/${encodeURIComponent(templateGuid)}/tenant-history`
            + `?corpSiteName=${encodeURIComponent(corpSiteName)}`
            + `&clientId=${encodeURIComponent(String(clientId))}`;
        const response = await getAuthenticatedFetch()(url);
        if (!response.ok) throw new Error(await this.extractError(response, `History failed (${response.status})`));
        return await response.json();
    }

    async downloadTemplate(templateGuid: string): Promise<{ blob: Blob; filename: string }> {
        const response = await getAuthenticatedFetch()(
            `${baseUrl}/templates/${encodeURIComponent(templateGuid)}/download`
        );
        if (!response.ok) throw new Error(await this.extractError(response, `Download failed (${response.status})`));
        const contentDisposition = response.headers.get('content-disposition');
        let filename = `template-${templateGuid}.json`;
        if (contentDisposition) {
            const utf8Match = /filename\*=UTF-8''([^;]+)/.exec(contentDisposition);
            if (utf8Match && utf8Match[1]) filename = decodeURIComponent(utf8Match[1]);
            else {
                const plainMatch = /filename="?([^";]+)"?/.exec(contentDisposition);
                if (plainMatch && plainMatch[1]) filename = plainMatch[1];
            }
        }
        const blob = await response.blob();
        return { blob, filename };
    }

    // ─── Provisioning ───
    async provisionAll(templateGuid: string, forceRePush: boolean): Promise<{ jobGuid: string; status: string }> {
        const response = await getAuthenticatedFetch()(
            `${baseUrl}/templates/${encodeURIComponent(templateGuid)}/provision-all`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ forceRePush }),
            }
        );
        if (!response.ok) throw new Error(await this.extractError(response, `Provision-all failed (${response.status})`));
        return await response.json();
    }

    async provisionTenant(
        templateGuid: string,
        corpSiteName: string,
        clientGuidId: string,
        forceRePush: boolean
    ): Promise<TemplateProvisioningResult> {
        const response = await getAuthenticatedFetch()(
            `${baseUrl}/templates/${encodeURIComponent(templateGuid)}/provision-tenant`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ corpSiteName, clientGuidId, forceRePush }),
            }
        );
        if (!response.ok) throw new Error(await this.extractError(response, `Provision-tenant failed (${response.status})`));
        return await response.json();
    }

    async getJobStatus(jobGuid: string): Promise<BulkProvisioningJobStatusDto> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/jobs/${encodeURIComponent(jobGuid)}`);
        if (!response.ok) throw new Error(await this.extractError(response, `Job status failed (${response.status})`));
        return await response.json();
    }

    async listEligibleTenants(): Promise<EligibleTenantDto[]> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/eligible-tenants`);
        if (!response.ok) throw new Error(await this.extractError(response, `Eligible tenants failed (${response.status})`));
        return await response.json();
    }

    // ─── Sync ───
    async syncTenants(): Promise<SurveyJSSyncMetadataDto> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/sync`, { method: "POST" });
        if (!response.ok) throw new Error(await this.extractError(response, `Sync failed (${response.status})`));
        return await response.json();
    }

    async syncTenant(corpSiteName: string, clientGuidId: string): Promise<number> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/sync-tenant`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ corpSiteName, clientGuidId }),
        });
        if (!response.ok) throw new Error(await this.extractError(response, `Sync tenant failed (${response.status})`));
        const text = await response.text();
        return text ? Number(text) : 0;
    }

    async getSyncStatus(): Promise<SurveyJSSyncMetadataDto | null> {
        const response = await getAuthenticatedFetch()(`${baseUrl}/sync-status`);
        if (!response.ok) throw new Error(await this.extractError(response, `Sync status failed (${response.status})`));
        const text = await response.text();
        if (!text) return null;
        return JSON.parse(text) as SurveyJSSyncMetadataDto;
    }

    private async extractError(response: Response, fallback: string): Promise<string> {
        try {
            const body = await response.json();
            if (body && typeof body.message === "string") return body.message;
        } catch { }
        return fallback;
    }
}

export default new SurveyJSTemplateService();
